import {
    createContext,
    useState,
    useContext,
    PropsWithChildren,
    useRef,
    useEffect
} from "react";

import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

export interface Source {
    title: string;
    url: string;
}

export const csmAiBaseUrl =
    process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"] ||
    "https://www.atlassian.com/gateway/api";
export const cloudId =
    process.env["SEARCH_AI_SUMMARY_CLOUD_SITE_ID"] ||
    "a35630ef-8504-4f3d-8e29-a2617e5d5b30";
export const agentId =
    process.env["SEARCH_AI_SUMMARY_AGENT_ID"] ||
    "3b8ff012-b386-319c-9ff3-f9cda66cb98e";

export type AISummaryContextType = {
    summary: string;
    sources: Source[] | undefined;
    conversationId: string;
    messageId: string;
    isLoading: boolean;
    traceResponse: string | undefined;
};

const AISummaryContext = createContext<AISummaryContextType | null>(null);

export const useAISummaryContext = (): AISummaryContextType => {
    const context = useContext(AISummaryContext);
    if (!context) {
        throw new Error(
            "useAISummaryContext must be used within a AISummaryContextProvider"
        );
    }
    return context;
};

interface AISummaryContextProviderProps extends PropsWithChildren {
    query: string;
}

export const AISummaryContextProvider = ({
    query,
    children
}: AISummaryContextProviderProps) => {
    const [summary, setSummary] = useState<string>("");
    const [sources, setSources] = useState<Source[] | undefined>(undefined);
    const [conversationId, setConversationId] = useState<string>("");
    const [messageId, setMessageId] = useState<string>("");
    const [isLoading, setIsLoading] = useState(true);
    const [traceResponse, setTraceResponse] = useState<string | undefined>(
        undefined
    );

    const abortControllerRef = useRef<AbortController | null>(null);
    const isFirstResponseRef = useRef(true);

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    // eslint-disable-next-line complexity
    const processLine = (line: string) => {
        try {
            const parsed = JSON.parse(line);
            if (parsed.type === "TRACE" && parsed.message?.content) {
                // TRACE contains the initial loading messages
                let traceContent = parsed.message.content;

                if (traceContent.includes(": ")) {
                    traceContent = traceContent.split(": ")[1];
                }
                if (traceContent.endsWith(".")) {
                    traceContent = traceContent.slice(0, -1);
                }
                setTraceResponse(traceContent);
            } else if (
                parsed.type === "FINAL_RESPONSE" &&
                (parsed.message?.sources || parsed.message?.message.sources)
            ) {
                // FINAL_RESPONSE contains the entire summary, sources, and additional info LLM info
                const sourcesData = (parsed.message?.sources ||
                    parsed.message?.message?.sources) as Source[];

                if (!sourcesData || sourcesData.length === 0) {
                    createAnalyticsEvent({
                        eventName: "searchAiSummaryNoSources",
                        eventType: "track",
                        action: "failed",
                        actionSubject: "searchAiSummary",
                        actionSubjectId: "searchAiSummaryFinalResponseLoaded",
                        source: "searchPage",
                        attributes: {
                            query: query,
                            conversationId: conversationId
                        }
                    }).fire();
                }

                if (parsed.message?.id) {
                    setMessageId(parsed.message?.message.id);
                }

                setSources(sourcesData);
                setTraceResponse(undefined);
            } else if (
                parsed.type === "ANSWER_PART" &&
                parsed.message?.content
            ) {
                setSummary((prev) => prev + parsed.message.content);
                setTraceResponse(undefined);
            }
        } catch (error) {
            console.warn("Failed to parse line as JSON:", line);
            setSummary((prev) => prev + "\n" + line);
        }
    };

    // eslint-disable-next-line complexity
    const fetchAISummary = async (searchQuery: string) => {
        setIsLoading(true);
        setTraceResponse(undefined);
        setSources(undefined);
        setSummary("");
        setMessageId("");
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        isFirstResponseRef.current = true;

        // Generate a new conversation ID for each new query
        const newConversationId = uuidv4();
        setConversationId(newConversationId);

        const userContext = {
            user_locale: intl.locale || "en",
            current_time: new Date().toISOString()
        };
        const queryWithUserContext = `${searchQuery} \n ${JSON.stringify(
            userContext
        )}`;

        let reader = null;
        try {
            const response = await fetch(
                `${csmAiBaseUrl}/assist/csm/v2/chat/channel/${newConversationId}/message/stream`,
                {
                    method: "POST",
                    credentials: "omit",
                    signal: abortControllerRef.current?.signal,
                    headers: {
                        "x-cloudid": cloudId,
                        "x-product": "jira",
                        "x-experience-id": "csm-ai",
                        "content-type": "application/json;charset=UTF-8"
                    },
                    body: JSON.stringify({
                        content: {
                            version: 1,
                            type: "doc",
                            content: [
                                {
                                    type: "paragraph",
                                    content: [
                                        {
                                            type: "text",
                                            text: queryWithUserContext
                                        }
                                    ]
                                }
                            ]
                        },
                        recipient_agent_named_id: "atlassian_wac_agent",
                        agent_id: agentId,
                        mimeType: "text/adf",
                        store_message: false,
                        citations_enabled: true
                    })
                }
            );

            createAnalyticsEvent({
                eventName: "aiSummary",
                eventType: "track",
                action: "loaded",
                actionSubject: "searchAiSummary",
                actionSubjectId: "searchAiSummaryLoaded",
                source: "searchPage",
                eventContainer: "load",
                attributes: {
                    responseCode: response.status,
                    responseBodyAvailable: response.body ? true : false
                }
            }).fire();

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (!response.body) {
                throw new Error("Response body is not available for streaming");
            }

            reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    if (buffer.trim()) {
                        processLine(buffer.trim());
                        setIsLoading(false);
                    }
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;

                const lines = buffer.split("\n");
                buffer = lines.pop() || "";

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (trimmedLine) {
                        processLine(trimmedLine);
                        setIsLoading(false);
                    }
                }
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
                return;
            }
            console.error("Error processing AI summary stream:", error);
            setSummary(
                (prev) =>
                    prev +
                    "\nAtlassian Intelligence can't provide a response right now."
            );
        } finally {
            reader?.releaseLock();
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAISummary(query);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    return (
        <AISummaryContext.Provider
            value={{
                summary,
                sources,
                conversationId,
                messageId,
                isLoading,
                traceResponse
            }}
        >
            {children}
        </AISummaryContext.Provider>
    );
};
